/** Stable identifiers shared between the Resume and Portfolio sections. */
export type PortfolioProjectId = "omen" | "aiveronica" | "2vault" | "intelliguard";

export const PROJECT_HIGHLIGHT_DURATION_MS = 3000;

const SCROLL_SETTLE_FALLBACK_MS = 1200;

let cancelPendingScroll: (() => void) | null = null;

/**
 * Scrolls the matching portfolio card into view and invokes `onSettled` once the
 * scroll has finished, so the highlight is not spent while the card is still
 * off-screen. Honors reduced-motion by jumping instead of animating.
 */
export function scrollToProject(projectId: PortfolioProjectId, onSettled: () => void): void {
    if (typeof document === "undefined") {
        return;
    }

    // A second click supersedes the first — never highlight a stale card.
    cancelPendingScroll?.();

    const target =
        document.querySelector(`[data-project-id="${projectId}"]`) ??
        document.getElementById("portfolio");

    if (!target) {
        // No card and no portfolio section (unmounted route, renamed or unrendered id).
        // Surface it instead of failing silently, and let the caller's pending state resolve.
        console.error(
            `scrollToProject: no [data-project-id="${projectId}"] card and no #portfolio element found.`,
        );
        onSettled();
        return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const startScrollY = window.scrollY;

    target.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "center",
    });

    // Nothing to wait for: the jump already happened, or the page cannot scroll further.
    if (prefersReducedMotion) {
        onSettled();
        return;
    }

    let settled = false;
    const finish = () => {
        if (settled) {
            return;
        }
        settled = true;
        cleanup();
        onSettled();
    };

    const timeoutId = window.setTimeout(finish, SCROLL_SETTLE_FALLBACK_MS);
    const supportsScrollEnd = "onscrollend" in window;

    function cleanup() {
        window.clearTimeout(timeoutId);
        if (supportsScrollEnd) {
            window.removeEventListener("scrollend", finish);
        }
        cancelPendingScroll = null;
    }

    if (supportsScrollEnd) {
        window.addEventListener("scrollend", finish);
    }

    cancelPendingScroll = () => {
        settled = true;
        cleanup();
    };

    // If the target was already in view the scroll is a no-op and `scrollend`
    // never fires; resolve on the next frame instead of burning the fallback.
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            if (window.scrollY === startScrollY) {
                finish();
            }
        });
    });
}
